import { StateCreator } from 'zustand';
import { produce } from 'immer';
import * as GeoJSON from 'geojson';
import type { GeoDataState, BBox } from './types'; // Importa BBox

// Stato per la funzionalità di estrazione delle curve di livello
export interface ContoursState {
  isEnabled: boolean;
  selectedArea: BBox | null; // Modificato: usa BBox [minLng, minLat, maxLng, maxLat]
  interval: number; // Intervallo tra le curve
  unit: 'meters' | 'feet'; // Unità di misura dell'intervallo
  status: 'idle' | 'fetching' | 'loading-dem' | 'generating' | 'success' | 'error'; // Stato del processo (rimosso 'drawing')
  errorMessage: string | null; // Messaggio di errore
  resultGeojson: GeoJSON.FeatureCollection<GeoJSON.LineString> | null; // Risultato GeoJSON delle curve
}

// Azioni per la gestione dello stato delle curve di livello
export interface ContoursSlice extends ContoursState {
  // Azioni sincrone
  enableDrawingMode: () => void;
  disableDrawingMode: () => void;
  setSelectedArea: (area: BBox | null) => void; // Modificato: accetta BBox
  setInterval: (interval: number) => void;
  setUnit: (unit: 'meters' | 'feet') => void;
  clearResult: () => void;
  setStatus: (status: ContoursState['status'], errorMessage?: string | null) => void; // Modificato per accettare errore
  // Azione asincrona
  generateContours: () => Promise<void>;
}

const initialState: ContoursState = {
  isEnabled: false,
  selectedArea: null,
  interval: 10,
  unit: 'meters',
  status: 'idle',
  errorMessage: null,
  resultGeojson: null,
};

// Funzione per creare la slice Zustand
export const createContoursSlice: StateCreator<
  GeoDataState, // Usa lo stato combinato
  [],
  [],
  ContoursSlice // Specifica il tipo restituito dalla slice
> = (set, get) => { // Aggiunto get per accedere allo stato nell'azione asincrona
  let workerInstance: Worker | null = null; // Riferimento al worker per la terminazione

  return {
  ...initialState,

  enableDrawingMode: () =>
    set(
      produce((state: ContoursSlice) => {
        state.isEnabled = true;
        state.selectedArea = null; // Resetta l'area quando si inizia a disegnare
        state.errorMessage = null;
        state.resultGeojson = null;
      })
    ),

  disableDrawingMode: () =>
    set(
      produce((state: ContoursSlice) => {
        state.isEnabled = false;
        // Non resettare lo status qui, potrebbe essere 'success' o 'error'
      })
    ),

  setSelectedArea: (area) =>
    set(
      produce((state: ContoursSlice) => {
        state.selectedArea = area;
        state.isEnabled = false; // Disabilita la modalità disegno dopo la selezione
        state.status = area ? 'idle' : state.status; // Torna a idle se l'area è valida
        state.errorMessage = null;
      })
    ),

  setInterval: (interval) =>
    set(
      produce((state: ContoursSlice) => {
        state.interval = interval > 0 ? interval : 1; // Assicura un intervallo positivo
      })
    ),

  setUnit: (unit) =>
    set(
      produce((state: ContoursSlice) => {
        state.unit = unit;
      })
    ),

  clearResult: () =>
    set(
      produce((state: ContoursSlice) => {
        state.resultGeojson = null;
        state.status = 'idle';
        state.errorMessage = null;
        state.selectedArea = null; // Potrebbe essere utile resettare anche l'area
        state.isEnabled = false;
      })
    ),

  setStatus: (status, errorMessage = null) => // Modificato per accettare errorMessage
    set(
      produce((state: ContoursSlice) => {
        state.status = status;
        state.errorMessage = errorMessage ?? null; // Imposta il messaggio di errore
        if (status !== 'error' && !errorMessage) {
          state.errorMessage = null; // Pulisci l'errore se lo stato non è 'error' e non c'è messaggio
        }
        if (status === 'idle' || status === 'success' || status === 'error') {
          state.isEnabled = false; // Disabilita il disegno se non siamo in 'drawing' o 'fetching' etc.
        }
      })
    ),

  generateContours: async () => {
    const { selectedArea, interval, unit, setStatus} = get();

    // 0. Termina worker precedente se esiste
    if (workerInstance) {
      console.log('Terminating previous contours worker...');
      workerInstance.terminate();
      workerInstance = null;
    }

    // 1. Verifica preliminare
    if (!selectedArea) {
      setStatus('error', 'Area non selezionata.');
      console.error('Generate contours error: Selected area is missing.');
      return;
    }

    // 2. Imposta stato iniziale e pulisci risultati precedenti
    setStatus('fetching');
    // Non chiamare clearResult() qui per mantenere l'area selezionata visibile
    set(produce((state: ContoursSlice) => {
        state.resultGeojson = null;
        state.errorMessage = null;
    }));


    try {
      // 3. Istanzia Worker
      // Nota: Assicurati che vite.config.ts sia configurato per i worker
      // Esempio: worker: { format: 'es' }
      console.log('Creating contours worker...');
      workerInstance = new Worker(new URL('../features/contours/utils/contours.worker.ts', import.meta.url), { type: 'module' });

      // 4. Listener Messaggi Worker
      workerInstance.onmessage = (event: MessageEvent) => {
        console.log('Message received from contours worker:', event.data);
        const { type, payload } = event.data;

        switch (type) {
          case 'success':
            set(produce((state: ContoursSlice) => {
                state.resultGeojson = payload as GeoJSON.FeatureCollection<GeoJSON.LineString>;
                state.status = 'success';
                state.errorMessage = null;
            }));
            console.log('Contours generated successfully.');
            if (workerInstance) {
              workerInstance.terminate();
              workerInstance = null;
            }
            break;
          case 'error':
            setStatus('error', payload || 'Errore durante la generazione delle curve.');
            console.error('Error message from contours worker:', payload);
            if (workerInstance) {
              workerInstance.terminate();
              workerInstance = null;
            }
            break;
          case 'status':
             // Aggiorna solo lo status, mantenendo il messaggio di errore se presente
             set(produce((state: ContoursSlice) => {
                if (payload === 'loading-dem' || payload === 'generating') {
                    state.status = payload;
                } else {
                    console.warn(`Received unknown status from worker: ${payload}`);
                }
             }));
            break;
          default:
            console.warn('Unknown message type received from worker:', type);
        }
      };

      // 5. Listener Errori Worker
      workerInstance.onerror = (error) => {
        console.error('Unhandled error in contours worker:', error);
        setStatus('error', `Errore imprevisto nel worker: ${error.message}`);
        if (workerInstance) {
          workerInstance.terminate();
          workerInstance = null;
        }
      };

      // 6. Invia Messaggio al Worker
      const zoomLevel = 13; // Livello di zoom fisso per il DEM, come da piano
      const workerParams = {
        bbox: selectedArea,
        interval,
        unit,
        zoomLevel,
      };
      console.log('Posting message to contours worker:', workerParams);
      workerInstance.postMessage(workerParams);

    } catch (error) {
      console.error('Failed to initialize or communicate with contours worker:', error);
      setStatus('error', `Errore nell'inizializzazione del worker: ${error instanceof Error ? error.message : String(error)}`);
      if (workerInstance) {
        workerInstance.terminate();
        workerInstance = null;
      }
    }
  },
  }; // Chiusura dell'oggetto ritornato
}; // Chiusura di createContoursSlice